import type { Provider, TransactionRequest } from '@ethersproject/providers';
import type { Overrides, Signer } from 'ethers';
import { Contract, ContractFactory, utils } from 'ethers';

import type { MogulNFT, MogulNFTInterface } from '../MogulNFT';

const _abi = [
  {
    inputs: [
      {
        internalType: 'address',
        name: '_admin',
        type: 'address',
      },
    ],
    stateMutability: 'nonpayable',
    type: 'constructor',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'operator',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'bool',
        name: 'approved',
        type: 'bool',
      },
    ],
    name: 'ApprovalForAll',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'previousOwner',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'newOwner',
        type: 'address',
      },
    ],
    name: 'OwnershipTransferred',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'previousAdminRole',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'newAdminRole',
        type: 'bytes32',
      },
    ],
    name: 'RoleAdminChanged',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'sender',
        type: 'address',
      },
    ],
    name: 'RoleGranted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'sender',
        type: 'address',
      },
    ],
    name: 'RoleRevoked',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'operator',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'from',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'to',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'uint256[]',
        name: 'ids',
        type: 'uint256[]',
      },
      {
        indexed: false,
        internalType: 'uint256[]',
        name: 'values',
        type: 'uint256[]',
      },
    ],
    name: 'TransferBatch',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: true,
        internalType: 'address',
        name: 'operator',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'from',
        type: 'address',
      },
      {
        indexed: true,
        internalType: 'address',
        name: 'to',
        type: 'address',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        indexed: false,
        internalType: 'uint256',
        name: 'value',
        type: 'uint256',
      },
    ],
    name: 'TransferSingle',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      {
        indexed: false,
        internalType: 'string',
        name: 'value',
        type: 'string',
      },
      {
        indexed: true,
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
    ],
    name: 'URI',
    type: 'event',
  },
  {
    inputs: [],
    name: 'DEFAULT_ADMIN_ROLE',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'ROLE_ADMIN',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
    ],
    name: 'balanceOf',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address[]',
        name: 'accounts',
        type: 'address[]',
      },
      {
        internalType: 'uint256[]',
        name: 'ids',
        type: 'uint256[]',
      },
    ],
    name: 'balanceOfBatch',
    outputs: [
      {
        internalType: 'uint256[]',
        name: '',
        type: 'uint256[]',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'value',
        type: 'uint256',
      },
    ],
    name: 'burn',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        internalType: 'uint256[]',
        name: 'ids',
        type: 'uint256[]',
      },
      {
        internalType: 'uint256[]',
        name: 'values',
        type: 'uint256[]',
      },
    ],
    name: 'burnBatch',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
    ],
    name: 'getRoleAdmin',
    outputs: [
      {
        internalType: 'bytes32',
        name: '',
        type: 'bytes32',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'grantRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'hasRole',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
      {
        internalType: 'address',
        name: 'operator',
        type: 'address',
      },
    ],
    name: 'isApprovedForAll',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'to',
        type: 'address',
      },
      {
        internalType: 'uint256[]',
        name: 'tokenIds',
        type: 'uint256[]',
      },
      {
        internalType: 'uint256[]',
        name: 'amounts',
        type: 'uint256[]',
      },
      {
        internalType: 'string[]',
        name: 'URIs',
        type: 'string[]',
      },
      {
        internalType: 'bytes',
        name: 'data',
        type: 'bytes',
      },
    ],
    name: 'mintBatch',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address[]',
        name: 'to',
        type: 'address[]',
      },
      {
        internalType: 'uint256[]',
        name: 'tokenIds',
        type: 'uint256[]',
      },
      {
        internalType: 'uint256[]',
        name: 'amounts',
        type: 'uint256[]',
      },
      {
        internalType: 'string[]',
        name: 'URIs',
        type: 'string[]',
      },
      {
        internalType: 'uint256[]',
        name: 'numRecipientsPerToken',
        type: 'uint256[]',
      },
      {
        internalType: 'bytes',
        name: 'data',
        type: 'bytes',
      },
    ],
    name: 'mintBatchMultipleRecipients',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'recipient',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'tokenId',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
      {
        internalType: 'string',
        name: 'URI',
        type: 'string',
      },
      {
        internalType: 'bytes',
        name: 'data',
        type: 'bytes',
      },
    ],
    name: 'mintToken',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'owner',
    outputs: [
      {
        internalType: 'address',
        name: '',
        type: 'address',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'renounceOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'renounceRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes32',
        name: 'role',
        type: 'bytes32',
      },
      {
        internalType: 'address',
        name: 'account',
        type: 'address',
      },
    ],
    name: 'revokeRole',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'from',
        type: 'address',
      },
      {
        internalType: 'address',
        name: 'to',
        type: 'address',
      },
      {
        internalType: 'uint256[]',
        name: 'ids',
        type: 'uint256[]',
      },
      {
        internalType: 'uint256[]',
        name: 'amounts',
        type: 'uint256[]',
      },
      {
        internalType: 'bytes',
        name: 'data',
        type: 'bytes',
      },
    ],
    name: 'safeBatchTransferFrom',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'from',
        type: 'address',
      },
      {
        internalType: 'address',
        name: 'to',
        type: 'address',
      },
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
      {
        internalType: 'uint256',
        name: 'amount',
        type: 'uint256',
      },
      {
        internalType: 'bytes',
        name: 'data',
        type: 'bytes',
      },
    ],
    name: 'safeTransferFrom',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'operator',
        type: 'address',
      },
      {
        internalType: 'bool',
        name: 'approved',
        type: 'bool',
      },
    ],
    name: 'setApprovalForAll',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'bytes4',
        name: 'interfaceId',
        type: 'bytes4',
      },
    ],
    name: 'supportsInterface',
    outputs: [
      {
        internalType: 'bool',
        name: '',
        type: 'bool',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'totalSupply',
    outputs: [
      {
        internalType: 'uint256',
        name: '',
        type: 'uint256',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'address',
        name: 'newOwner',
        type: 'address',
      },
    ],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      {
        internalType: 'uint256',
        name: 'id',
        type: 'uint256',
      },
    ],
    name: 'uri',
    outputs: [
      {
        internalType: 'string',
        name: '',
        type: 'string',
      },
    ],
    stateMutability: 'view',
    type: 'function',
  },
];

const _bytecode =
  '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';

export class MogulNFTFactory extends ContractFactory {
  constructor(
    ...args: [signer: Signer] | ConstructorParameters<typeof ContractFactory>
  ) {
    if (args.length === 1) {
      super(_abi, _bytecode, args[0]);
    } else {
      super(...args);
    }
  }

  deploy(
    _admin: string,
    overrides?: Overrides & { from?: string | Promise<string> },
  ): Promise<MogulNFT> {
    return super.deploy(_admin, overrides ?? {}) as Promise<MogulNFT>;
  }
  getDeployTransaction(
    _admin: string,
    overrides?: Overrides & { from?: string | Promise<string> },
  ): TransactionRequest {
    return super.getDeployTransaction(_admin, overrides ?? {});
  }
  attach(address: string): MogulNFT {
    return super.attach(address) as MogulNFT;
  }
  connect(signer: Signer): MogulNFTFactory {
    return super.connect(signer) as MogulNFTFactory;
  }
  static readonly bytecode = _bytecode;
  static readonly abi = _abi;
  static createInterface(): MogulNFTInterface {
    return new utils.Interface(_abi) as MogulNFTInterface;
  }
  static connect(
    address: string,
    signerOrProvider: Signer | Provider,
  ): MogulNFT {
    return new Contract(address, _abi, signerOrProvider) as MogulNFT;
  }
}
